/**
 * loyalty.js — helper terpusat untuk kalkulasi & pencatatan poin loyalty
 * Dipakai oleh PODetail (PO) dan bisa dipakai POS di kemudian hari
 */
import { supabase } from './supabase';

// ── Ambil semua setting loyalty dari DB ───────────────────────────
export async function getLoyaltySettings() {
  const { data } = await supabase
    .from('app_settings')
    .select('key,value')
    .in('key', [
      'loyalty_enabled',
      'points_per_rupiah',
      'welcome_bonus',
      'double_point_days',
    ]);

  const map = {};
  (data || []).forEach(r => { map[r.key] = r.value; });

  return {
    enabled:          map['loyalty_enabled']    === '1',
    pointsPerRupiah:  parseFloat(map['points_per_rupiah'] || '0.001'),
    welcomeBonus:     parseInt(map['welcome_bonus']        || '10', 10),
    doublePointDays:  (map['double_point_days'] || '')
      .split(',').filter(Boolean).map(Number), // [0-6], 0=Minggu
    expireDays:       parseInt(map['points_expire_days']  || '0',  10), // 0 = nonaktif
  };
}

/**
 * Hitung poin yang didapat dari satu transaksi
 * @param {number} totalRp  — nominal transaksi
 * @param {object} settings — hasil getLoyaltySettings()
 * @param {Date}   date     — tanggal transaksi (untuk cek double point)
 * @returns {number} poin (integer, dibulatkan ke bawah)
 */
/**
 * @param {boolean} allowDouble — false untuk PO, true untuk POS
 */
export function calcEarnPoints(totalRp, settings, date = new Date(), allowDouble = true) {
  if (!settings.enabled || !totalRp) return 0;
  const base = Math.floor(totalRp * settings.pointsPerRupiah);
  if (!allowDouble) return base;
  const dow      = date.getDay();
  const isDouble = settings.doublePointDays.includes(dow);
  return isDouble ? base * 2 : base;
}

/**
 * Catat poin ke loyalty_transactions + update customers.loyalty_points
 *
 * @param {object} params
 *   customerId   — uuid customer
 *   orderId      — id order (po_orders.id)
 *   orderType    — 'po' | 'pos'
 *   earnPoints   — poin dari transaksi (bisa 0)
 *   isNewCustomer— apakah customer baru (untuk welcome bonus)
 *   welcomeBonus — dari settings
 * @returns {object} { earnPoints, welcomePoints, totalAdded }
 */
export async function recordLoyaltyPoints({
  customerId,
  orderId,
  orderType = 'po',
  earnPoints,
  isNewCustomer,
  welcomeBonus,
}) {
  const rows    = [];
  let totalAdded = 0;

  if (earnPoints > 0) {
    rows.push({
      customer_id: customerId,
      order_id:    String(orderId),
      order_type:  orderType,
      type:        'earn',
      points:      earnPoints,
      note:        `Poin dari order ${orderType.toUpperCase()}`,
    });
    totalAdded += earnPoints;
  }

  const welcomePoints = (isNewCustomer && welcomeBonus > 0) ? welcomeBonus : 0;
  if (welcomePoints > 0) {
    rows.push({
      customer_id: customerId,
      order_id:    String(orderId),
      order_type:  orderType,
      type:        'welcome',
      points:      welcomePoints,
      note:        'Welcome bonus pelanggan baru',
    });
    totalAdded += welcomePoints;
  }

  if (rows.length === 0) return { earnPoints: 0, welcomePoints: 0, totalAdded: 0 };

  // Insert ke loyalty_transactions
  await supabase.from('loyalty_transactions').insert(rows);

  // Update loyalty_points di customers
  // Trigger fn_update_loyalty_tier akan otomatis update tier & lifetime
  await supabase.rpc('increment_loyalty_points', {
    p_customer_id: customerId,
    p_delta:       totalAdded,
  }).then(async ({ error }) => {
    // Jika RPC belum ada, fallback ke manual update
    if (error) {
      const { data: cust } = await supabase
        .from('customers').select('loyalty_points').eq('id', customerId).single();
      const current = cust?.loyalty_points || 0;
      await supabase.from('customers')
        .update({ loyalty_points: current + totalAdded })
        .eq('id', customerId);
    }
  });

  return { earnPoints, welcomePoints, totalAdded };
}

/**
 * Cek & expire poin customer yang tidak aktif melebihi expireDays.
 * Dipanggil saat halaman Customers dibuka (sekali per sesi).
 *
 * @param {Array}  customers  — list customer dari DB
 * @param {object} settings   — hasil getLoyaltySettings()
 * @returns {number} jumlah customer yang di-expire
 */
export async function expireStalePoints(customers, settings) {
  if (!settings.enabled || settings.expireDays <= 0) return 0;

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - settings.expireDays);

  // Filter customer yang: punya poin > 0 DAN last_seen_at/created_at sudah melewati cutoff
  const stale = customers.filter(c => {
    if ((c.loyalty_points || 0) <= 0) return false;
    const ref = c.last_seen_at || c.created_at;
    if (!ref) return false;
    return new Date(ref) < cutoff;
  });

  if (stale.length === 0) return 0;

  // Untuk setiap customer stale, catat expire & reset poin ke 0
  for (const c of stale) {
    // Catat ke loyalty_transactions
    await supabase.from('loyalty_transactions').insert({
      customer_id: c.id,
      order_id:    '',
      order_type:  'pos',
      type:        'expire',
      points:      c.loyalty_points,
      note:        `Poin hangus — tidak ada transaksi lebih dari ${settings.expireDays} hari`,
    });

    // Reset loyalty_points ke 0 (loyalty_lifetime tidak berubah — lifetime tidak pernah berkurang)
    await supabase.from('customers')
      .update({ loyalty_points: 0 })
      .eq('id', c.id);
  }

  return stale.length;
}
